// pageGuides.js
//
// One entry per page, read by two things: FirstVisitGuide.vue (the small
// one-time popup shown the first time a page is visited) and
// OnboardingTour.vue (the guided first-login walkthrough, for the pages
// on its route). There's no standalone "browse every page" screen anymore
// — that was replaced by the Help & Support FAQ page (see
// src/components/pagesFaq.js / pages/FaqPage.vue), since it just
// duplicated what the popup/tour already say. Keep entries here focused
// on what a page IS; how-do-I / troubleshooting content belongs in
// pagesFaq.js instead.
//
// Lookup: routes in router.js each carry meta.guideKey. Both consumers
// resolve the current page via that guideKey first (falling back to route
// name, then route path). If you add a new route, add
// meta: { guideKey: "yourKey" } to it and a matching entry below.

export const categoryOrder = [
  "Getting started",
  "Dashboard",
  "Properties",
  "Tenants",
  "Billing & payments",
  "Utility readings",
  "Communication",
  "Account",
  "Other",
];

export const pageGuides = {
  // ---- Getting started ----
  HomePage: {
    title: "Home",
    category: "Getting started",
    summary: "The public landing page — what visitors see before they sign in.",
    tips: ["Use 'Book a demo' here to send a prospective landlord to the sales form."],
  },
  LoginUser: {
    title: "Login",
    category: "Getting started",
    summary: "Sign in with your registered email and password to reach your dashboard.",
    tips: [
      "Use the eye icon to reveal your password if you're not sure what you typed.",
      "'Forgot password' below the form starts a reset by email.",
      "If your account shows as not verified, use the resend option to get a new code.",
    ],
  },
  registerUser: {
    title: "Register",
    category: "Getting started",
    summary: "Create a business account — business name, phone, email, location and P.O. Box — to start managing properties.",
    tips: [
      "Use a phone number and email you check regularly; verification and alerts go there.",
      "After registering you're taken to account verification before you can log in.",
    ],
  },
  verifyAccount: {
    title: "Verify account",
    category: "Getting started",
    summary: "Enter the verification code sent to your email or phone to activate your account.",
    tips: ["Didn't get a code? Use Send OTP to request a new one."],
  },
  SendOtp: {
    title: "Send OTP",
    category: "Getting started",
    summary: "Request a one-time verification code, used for account verification or password resets.",
    tips: ["The code expires after a short time — request a new one if it stops working."],
  },
  forgotPassword: {
    title: "Forgot password",
    category: "Getting started",
    summary: "Enter your email to start resetting a forgotten password.",
    tips: ["You'll get an email to continue to the reset password page."],
  },
  passwordreset: {
    title: "Reset password",
    category: "Getting started",
    summary: "Set a new password (at least 8 characters) after opening the link from your reset email.",
    tips: ["An expired or already-used link shows as invalid — request a fresh one from Forgot password."],
  },
  bookDemo: {
    title: "Book a demo",
    category: "Getting started",
    summary: "Public form for a prospective landlord to request a walkthrough of the platform — name, business, email and phone.",
    tips: [],
  },

  // ---- Dashboard ----
  DashBoard: {
    title: "Dashboard",
    category: "Dashboard",
    summary: "Your at-a-glance overview: total houses, units, occupied vs. vacant units, tenants, and monthly collections, plus recent activity.",
    tips: [
      "Use this as your starting point each day before drilling into a specific house or tenant.",
      "'Occupied' vs. 'Vacant' here reflects unit assignment — assign a tenant to move a unit from vacant to occupied.",
    ],
  },

  // ---- Properties ----
  HousesList: {
    title: "Houses",
    category: "Properties",
    summary: "Every property you manage. Open one to see its units and tenants.",
    tips: ["Use 'Add house' to register another property before adding its units."],
  },
  newHouse: {
    title: "Add a house",
    category: "Properties",
    summary: "Register a new property (house details) before you can add units to it.",
    tips: ["Add the house first, then add its units from the Houses list."],
  },
  UnitsList: {
    title: "Units",
    category: "Properties",
    summary: "Units for a house, or across all your properties — occupancy status at a glance.",
    tips: [
      "Occupied units show the current tenant; vacant ones are ready to assign.",
      "Open a vacant unit to assign a tenant to it.",
    ],
  },
  AddUnit: {
    title: "Add unit",
    category: "Properties",
    summary: "Add units to a house — a single unit (number, rent, type) using the form, or several at once with a CSV upload, from the same page.",
    tips: ["Switch to the bulk-upload tab if you're adding many units to the same house."],
  },
  uploadUnits: {
    title: "Upload units",
    category: "Properties",
    summary: "Bulk-create units for a house by uploading a CSV file instead of entering them by hand.",
    tips: ["Check the expected CSV column format before uploading, to avoid failed rows."],
  },
  uploadunitswithtenants: {
    title: "Upload units with tenants",
    category: "Properties",
    summary: "Bulk-create units and assign their tenants in one CSV upload — useful when migrating an existing property onto the platform.",
    tips: ["Each row should include both the unit details and the tenant to attach to it."],
  },

  // ---- Tenants ----
  TenantsList: {
    title: "Tenants",
    category: "Tenants",
    summary: "Everyone currently renting from you, across all your properties.",
    tips: ["Click a tenant to see their unit, balance and payment history."],
  },
  addTenant: {
    title: "Add tenant",
    category: "Tenants",
    summary: "Register a tenant one at a time (name, phone, ID), or bulk-add several by dropping a CSV file, from the same page.",
    tips: ["A tenant still needs to be assigned to a unit afterwards, from Assign tenant or the unit's page."],
  },
  editTenant: {
    title: "Edit tenant",
    category: "Tenants",
    summary: "Update a tenant's name, phone, ID, gender, marital status, or arrears amount.",
    tips: [],
  },
  AssignTenant: {
    title: "Assign tenant",
    category: "Tenants",
    summary: "Link an existing tenant to a vacant unit by searching their name or phone number — this marks the unit occupied.",
    tips: ["The unit must be vacant before you can assign someone to it."],
  },
  tenantDetails: {
    title: "Tenant details",
    category: "Tenants",
    summary: "Full profile for one tenant — unit, contact info, billing and payment history.",
    tips: [],
  },
  getTenantStatement: {
    title: "Tenant statement",
    category: "Tenants",
    summary: "Generate a statement of a tenant's charges and payments over a period, for their records.",
    tips: ["Useful when a tenant disputes a balance or needs proof of payment history."],
  },

  // ---- Billing & payments ----
  addBilling: {
    title: "Add billing",
    category: "Billing & payments",
    summary: "Create a new charge (bill) for a tenant — enter the house ID, tenant ID and amount.",
    tips: ["Double-check the house and tenant ID before submitting, since these are looked up by ID, not name."],
  },
  monthlyBillings: {
    title: "Monthly billings",
    category: "Billing & payments",
    summary: "See all billings generated for a given month, across tenants. Use Refresh data if a new bill doesn't show up yet.",
    tips: [],
  },
  getBills: {
    title: "Get bills",
    category: "Billing & payments",
    summary: "Look up bills — including water/utility bills — by searching a unit or tenant.",
    tips: [],
  },
  Invoices: {
    title: "Invoices",
    category: "Billing & payments",
    summary: "All invoices issued to tenants, with their payment status. Search to find a specific one.",
    tips: [],
  },
  invoiceDetails: {
    title: "Invoice details",
    category: "Billing & payments",
    summary: "The line items and total for a single invoice, formatted for printing or sharing with the tenant.",
    tips: [],
  },
  Payments: {
    title: "Payments",
    category: "Billing & payments",
    summary: "A record of payments received across all tenants.",
    tips: [],
  },
  receivePayments: {
    title: "Receive payment",
    category: "Billing & payments",
    summary: "Record a payment from a tenant — search them by name or ID, then enter the amount received.",
    tips: ["Double-check the tenant before submitting — this updates their balance immediately."],
  },
  createPaybill: {
    title: "Create paybill",
    category: "Billing & payments",
    summary: "Set up a paybill or till number (e.g. M-Pesa) so tenants can pay you directly. Leave the shortcode blank if you're using a standard Safaricom paybill.",
    tips: [],
  },

  // ---- Utility readings ----
  electricityReadings: {
    title: "Electricity readings",
    category: "Utility readings",
    summary: "Record electricity meter readings per unit for a house, either by hand or by file upload, used to bill tenants for usage.",
    tips: ["Enter readings consistently each cycle so usage-based billing stays accurate."],
  },
  waterReadings: {
    title: "Water readings",
    category: "Utility readings",
    summary: "Record water meter readings per unit for a house, either by hand or by file upload, used to bill tenants for usage.",
    tips: ["Enter readings consistently each cycle so usage-based billing stays accurate."],
  },

  // ---- Communication ----
  smsHistory: {
    title: "SMS history",
    category: "Communication",
    summary: "A log of SMS messages sent to tenants. Filter by phone number, message text, or date range.",
    tips: [],
  },
  tenantsSms: {
    title: "Message tenants",
    category: "Communication",
    summary: "Send an SMS to one or more tenants — search and tick who should receive it, then write your message.",
    tips: ["Useful for rent reminders or announcements to a whole property."],
  },

  // ---- Account ----
  profile: {
    title: "Profile",
    category: "Account",
    summary: "View your business account details — business info, paybill/payment setup, and address.",
    tips: [],
  },
  updateprofile: {
    title: "Update profile",
    category: "Account",
    summary: "Edit your business name, phone, address, and paybill account details.",
    tips: [],
  },

  // ---- Other ----
  Help: {
    title: "Help & Support",
    category: "Other",
    summary: "Search common issues and how to fix them, or email support directly.",
    tips: [],
  },
};
